import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StatsCard from '../admin-dashboard/components/StatsCard';
import SubscriptionStatusDashboard from './components/SubscriptionStatusDashboard';
import PaymentProcessingPanel from './components/PaymentProcessingPanel';
import RevenueAnalytics from './components/RevenueAnalytics';
import BillingAlerts from './components/BillingAlerts';
import DisputeResolution from './components/DisputeResolution';

const BillingSubscriptionCenter = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, isSuperAdmin, loading: authLoading } = useAuth();
  const [stats, setStats] = useState({
    monthlyRecurringRevenue: 0,
    activeSubscriptions: 0,
    pendingPayments: 0,
    failedTransactions: 0,
    paymentSuccessRate: 0,
    churnRate: 0
  });
  const [subscriptions, setSubscriptions] = useState([]);
  const [billingRecords, setBillingRecords] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState('30');

  useEffect(() => {
    if (!authLoading && (!user || (!isGymOwner && !isSuperAdmin))) {
      navigate('/member-dashboard');
    }
  }, [user, isGymOwner, isSuperAdmin, authLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id && (isGymOwner || isSuperAdmin)) {
      loadBillingData();
    }
  }, [currentTenant, isGymOwner, isSuperAdmin, dateRange]);

  const loadBillingData = async () => {
    setLoading(true);
    setError('');

    try {
      const daysAgo = parseInt(dateRange);
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - daysAgo);

      // Load subscriptions
      const { data: subscriptionsData, error: subscriptionsError } = await supabase?.from('member_subscriptions')?.select(`
          *,
          user_profiles:member_id(id, full_name, email, avatar_url),
          membership_plans:plan_id(id, plan_name, plan_code, price)
        `)?.eq('tenant_id', currentTenant?.id)?.order('created_at', { ascending: false });

      if (subscriptionsError) throw subscriptionsError;
      setSubscriptions(subscriptionsData || []);

      // Load billing records
      const { data: billingData, error: billingError } = await supabase?.from('billing_records')?.select('*')?.eq('tenant_id', currentTenant?.id)?.gte('billing_date', startDate?.toISOString()?.split('T')?.[0])?.order('billing_date', { ascending: false });

      if (billingError) throw billingError;
      setBillingRecords(billingData || []);

      // Calculate stats
      const activeSubscriptions = subscriptionsData?.filter(s => s?.status === 'active')?.length || 0;
      const monthlyRecurringRevenue = subscriptionsData
        ?.filter(s => s?.status === 'active')
        ?.reduce((sum, s) => sum + parseFloat(s?.final_price || 0), 0) || 0;

      const pendingPayments = billingData?.filter(b => b?.payment_status === 'pending')?.length || 0;
      const failedTransactions = billingData?.filter(b => b?.payment_status === 'failed')?.length || 0;
      const completedPayments = billingData?.filter(b => b?.payment_status === 'completed')?.length || 0;
      const totalPayments = billingData?.length || 1;
      const paymentSuccessRate = (completedPayments / totalPayments) * 100;

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo?.setDate(thirtyDaysAgo?.getDate() - 30);
      const cancelledThisMonth = subscriptionsData?.filter(
        s => s?.cancellation_date && new Date(s?.cancellation_date) >= thirtyDaysAgo
      )?.length || 0;
      const churnRate = activeSubscriptions > 0 ? (cancelledThisMonth / activeSubscriptions) * 100 : 0;

      setStats({
        monthlyRecurringRevenue,
        activeSubscriptions,
        pendingPayments,
        failedTransactions,
        paymentSuccessRate,
        churnRate
      });

      // Generate alerts
      const alertsList = [];

      if (failedTransactions > 0) {
        alertsList?.push({
          id: 'failed-payments',
          type: 'error',
          title: 'Failed Payments',
          message: `${failedTransactions} payment${failedTransactions > 1 ? 's' : ''} failed - requires immediate attention`,
          priority: 'critical'
        });
      }

      if (pendingPayments > 5) {
        alertsList?.push({
          id: 'pending-payments',
          type: 'warning',
          title: 'Pending Payments',
          message: `${pendingPayments} payment${pendingPayments > 1 ? 's' : ''} awaiting processing`,
          priority: 'high'
        });
      }

      if (paymentSuccessRate < 90) {
        alertsList?.push({
          id: 'low-success-rate',
          type: 'warning',
          title: 'Low Payment Success Rate',
          message: `Payment success rate at ${paymentSuccessRate?.toFixed(1)}% - review payment processing`,
          priority: 'medium'
        });
      }

      setAlerts(alertsList);

    } catch (err) {
      setError(err?.message || 'Failed to load billing data');
      console.error('Billing data load error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading billing data...</p>
          </div>
        </div>
        <MobileBottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-6 pb-24 md:pb-6">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
              Billing & Subscription Center
            </h1>
            <p className="text-muted-foreground">
              Comprehensive financial operations and subscription management
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e?.target?.value)}
              className="px-4 py-2 bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
            <Button
              onClick={loadBillingData}
              iconName="RefreshCw"
              variant="outline"
            >
              Refresh
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
              <p className="text-destructive font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Key Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
          <StatsCard
            icon="DollarSign"
            iconColor="var(--color-success)"
            title="Monthly Recurring Revenue"
            value={`${stats?.monthlyRecurringRevenue?.toFixed(2)}`}
            subtitle="MRR"
            trend="up"
            trendValue="+8%"
          />
          <StatsCard
            icon="CheckCircle"
            iconColor="var(--color-primary)"
            title="Active Subscriptions"
            value={stats?.activeSubscriptions}
            subtitle="Currently active"
            trend=""
            trendValue=""
          />
          <StatsCard
            icon="Clock"
            iconColor="var(--color-warning)"
            title="Pending Payments"
            value={stats?.pendingPayments}
            subtitle="Awaiting processing"
            trend=""
            trendValue=""
          />
          <StatsCard
            icon="XCircle"
            iconColor="var(--color-destructive)"
            title="Failed Transactions"
            value={stats?.failedTransactions}
            subtitle="Requires attention"
            trend=""
            trendValue=""
          />
          <StatsCard
            icon="TrendingUp"
            iconColor="var(--color-success)"
            title="Payment Success Rate"
            value={`${stats?.paymentSuccessRate?.toFixed(1)}%`}
            subtitle="Success rate"
            trend=""
            trendValue=""
          />
          <StatsCard
            icon="TrendingDown"
            iconColor="var(--color-destructive)"
            title="Churn Rate"
            value={`${stats?.churnRate?.toFixed(1)}%`}
            subtitle="Monthly churn"
            trend=""
            trendValue=""
          />
        </div>

        {/* Billing Alerts */}
        {alerts?.length > 0 && (
          <BillingAlerts alerts={alerts} onRefresh={loadBillingData} />
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
          <div className="xl:col-span-2">
            <SubscriptionStatusDashboard
              subscriptions={subscriptions}
              onRefresh={loadBillingData}
            />
          </div>
          <div>
            <RevenueAnalytics
              billingRecords={billingRecords}
              dateRange={dateRange}
            />
          </div>
        </div>

        {/* Payment Processing */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
          <PaymentProcessingPanel
            subscriptions={subscriptions}
            onRefresh={loadBillingData}
          />
          <DisputeResolution
            billingRecords={billingRecords}
            onRefresh={loadBillingData}
          />
        </div>
      </main>
      <MobileBottomNav />
    </div>
  );
};

export default BillingSubscriptionCenter;